// React
import { ReactElement, useEffect, useState } from 'react';
// Types
import { Slice } from '@utmb/types/slice-handler';
// Styles
import styles from './results.module.scss';
import { SliceResultsView } from '@utmb/types/index';
import RaceResultPaginatedView from '@utmb/types/view/RaceResultPaginatedView';
import { fetchTheAPI } from '@utmb/utils/utmb';
import { useRouter } from 'next/router';
import { useTenancy } from '@utmb/contexts/TenancyContext';
import { useUser } from '@utmb/contexts/AuthContext';
import { ResultsRaceSelector } from '@utmb/features/results/ResultsRaceSelector';
import { ResultsTable } from '@utmb/features/results/ResultsTable';
import { ResultsYearSelector } from '@utmb/features/results/ResultsYearSelector';

export const Results = (props: Slice<SliceResultsView>): ReactElement => {
    const { id, results } = props;

    const { locale, query } = useRouter();
    const { tenantId } = useTenancy();
    const { getToken, isLoginInProgress } = useUser();
    const [raceResults, setRaceResults] = useState<RaceResultPaginatedView>();
    const [options, setOptions] = useState<{ label: string; value: string }[]>();
    const [maxYear, setMaxYear] = useState<number | undefined>();

    const [isLoading, setIsLoading] = useState(false);

    async function getResults(): Promise<void> {
        setIsLoading(true);

        const token = await getToken();

        let offset = 0;
        const limit = 50;
        if (query.page) {
            const page = Number(query.page);
            if (page > 1) {
                offset = (page - 1) * Number(limit);
            }
        }

        let filterGender = '';
        if (query.gender) {
            filterGender = query.gender.toString();
        }

        const raceResults = (
            query.raceUri
                ? await fetchTheAPI({
                      tenantId,
                      isClient: true,
                      path: `/races/${query.raceUri}/results`,
                      params: { lang: locale as string, offset: offset.toString(), limit: limit.toString(), gender: filterGender },
                      bearer: token,
                  })
                : null
        ) as RaceResultPaginatedView;

        const options = Object.values(results).map((result) => ({
            label: result.year.toString(),
            value: result.year.toString(),
        }));
        const maxYear = options.reduce((r, b) => Math.max(r, Number(b.value)), 0);
        setMaxYear(maxYear);
        setRaceResults(raceResults);
        setOptions(options);
        setIsLoading(false);
    }

    useEffect(() => {
        isLoginInProgress ? null : getResults().then();
    }, [query.page, query.gender, query.raceUri, query.year, isLoginInProgress]);

    return (
        <section id={id} className={styles.resultsContainer}>
            <div className={styles.selectorMainContainer}>
                {options && maxYear ? <ResultsYearSelector options={options} maxYear={maxYear} /> : <></>}
                {maxYear ? <ResultsRaceSelector results={results} year={query.year ? query.year : maxYear.toString()} /> : <></>}
            </div>
            {raceResults ? <ResultsTable results={raceResults} isLoading={isLoading} /> : <></>}
        </section>
    );
};
